import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUnifiedAuth } from '../../hooks/useUnifiedAuth';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSystem = ({ provider, serviceType = 'consultation' }) => {
  const navigate = useNavigate();
  const { user: legacyUser } = useAuth();
  const { user: unifiedUser } = useUnifiedAuth();
  
  // Use unified auth if available, fallback to legacy
  const user = unifiedUser || legacyUser;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    serviceType: 'consultation',
    selectedService: null,
    selectedProvider: provider,
    selectedDoctor: null,
    selectedDate: null,
    selectedTime: null,
    patientInfo: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: '',
      gender: '',
      medicalHistory: '',
      symptoms: '',
      urgency: 'routine'
    },
    paymentMethod: 'card',
    totalAmount: 0,
    platformFee: 0,
    finalAmount: 0
  });

  // Update patient info when user data changes
  useEffect(() => {
    if (user) {
      setBookingData(prev => ({
        ...prev,
        patientInfo: {
          ...prev.patientInfo,
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || ''
        }
      }));
    }
  }, [user]);

  // Service categories with pricing
  const serviceCategories = {
    consultations: {
      title: '🩺 Medical Consultations',
      services: [
        { id: 'general-consultation', name: 'General Consultation', price: 75, duration: '30 min', description: 'Comprehensive health assessment and medical advice' },
        { id: 'specialist-consultation', name: 'Specialist Consultation', price: 150, duration: '45 min', description: 'Expert consultation with medical specialists' },
        { id: 'follow-up', name: 'Follow-up Consultation', price: 50, duration: '15 min', description: 'Review progress and adjust treatment plan' },
        { id: 'telemedicine', name: 'Video Consultation', price: 60, duration: '20 min', description: 'Remote consultation via video call' },
        { id: 'second-opinion', name: 'Second Opinion', price: 200, duration: '60 min', description: 'Expert second opinion on diagnosis and treatment' }
      ]
    },
    procedures: {
      title: '⚕️ Medical Procedures',
      services: [
        { id: 'blood-test', name: 'Comprehensive Blood Panel', price: 120, duration: '15 min', description: 'Complete blood count, metabolic panel, lipid profile' },
        { id: 'imaging-mri', name: 'MRI Scan', price: 800, duration: '60 min', description: 'Magnetic resonance imaging for detailed internal views' },
        { id: 'imaging-ct', name: 'CT Scan', price: 600, duration: '30 min', description: 'Computed tomography for cross-sectional body images' },
        { id: 'ultrasound', name: 'Ultrasound Examination', price: 150, duration: '30 min', description: 'Non-invasive imaging using sound waves' },
        { id: 'x-ray', name: 'X-Ray Imaging', price: 80, duration: '10 min', description: 'Standard radiographic imaging' },
        { id: 'ecg', name: 'Electrocardiogram (ECG)', price: 100, duration: '20 min', description: 'Heart rhythm and electrical activity monitoring' }
      ]
    },
    dental: {
      title: '🦷 Dental Services',
      services: [
        { id: 'dental-cleaning', name: 'Professional Teeth Cleaning', price: 120, duration: '60 min', description: 'Deep cleaning, plaque removal, and polishing' },
        { id: 'dental-whitening', name: 'Teeth Whitening Treatment', price: 300, duration: '90 min', description: 'Professional whitening for brighter smile' },
        { id: 'dental-filling', name: 'Dental Fillings', price: 180, duration: '45 min', description: 'Cavity treatment with composite fillings' },
        { id: 'root-canal', name: 'Root Canal Treatment', price: 800, duration: '120 min', description: 'Complete root canal therapy' },
        { id: 'dental-implant', name: 'Dental Implant Consultation', price: 200, duration: '60 min', description: 'Assessment and planning for dental implants' }
      ]
    },
    beauty: {
      title: '✨ Aesthetic & Beauty',
      services: [
        { id: 'botox', name: 'Botox Injections', price: 400, duration: '30 min', description: 'Anti-aging botox treatment for wrinkles' },
        { id: 'dermal-fillers', name: 'Dermal Fillers', price: 600, duration: '45 min', description: 'Facial volume restoration and contouring' },
        { id: 'microneedling', name: 'Microneedling Treatment', price: 250, duration: '60 min', description: 'Skin rejuvenation through collagen stimulation' },
        { id: 'chemical-peel', name: 'Chemical Peel', price: 200, duration: '45 min', description: 'Skin resurfacing for improved texture and tone' },
        { id: 'laser-hair-removal', name: 'Laser Hair Removal', price: 150, duration: '30 min', description: 'Permanent hair reduction treatment' }
      ]
    },
    wellness: {
      title: '🧘 Wellness & Prevention',
      services: [
        { id: 'health-checkup', name: 'Complete Health Checkup', price: 300, duration: '120 min', description: 'Comprehensive annual health assessment' },
        { id: 'nutrition-consultation', name: 'Nutritional Counseling', price: 100, duration: '45 min', description: 'Personalized diet and nutrition planning' },
        { id: 'mental-health', name: 'Mental Health Consultation', price: 150, duration: '50 min', description: 'Psychological assessment and counseling' },
        { id: 'physical-therapy', name: 'Physical Therapy Session', price: 120, duration: '60 min', description: 'Rehabilitation and movement therapy' },
        { id: 'vaccination', name: 'Vaccination Service', price: 50, duration: '15 min', description: 'Immunization and travel vaccines' }
      ]
    }
  };

  // Calculate pricing with platform fee
  useEffect(() => {
    if (bookingData.selectedService) {
      const basePrice = bookingData.selectedService.price;
      const platformFee = Math.round(basePrice * 0.15); // 15% platform fee
      const finalAmount = basePrice + platformFee;
      
      setBookingData(prev => ({
        ...prev,
        totalAmount: basePrice,
        platformFee,
        finalAmount
      }));
    }
  }, [bookingData.selectedService]);

  // Step 1: Service Selection
  const ServiceSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Service</h2>
        <p className="text-gray-600">Choose the medical service you need</p>
      </div>

      {Object.entries(serviceCategories).map(([categoryKey, category]) => (
        <div key={categoryKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{category.title}</h3>
          </div>
          
          <div className="p-6 space-y-4">
            {category.services.map(service => (
              <div
                key={service.id}
                onClick={() => {
                  setBookingData(prev => ({ 
                    ...prev, 
                    selectedService: service,
                    serviceType: categoryKey.slice(0, -1) // Remove 's' to get singular
                  }));
                  setCurrentStep(2);
                }}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                  bookingData.selectedService?.id === service.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900">{service.name}</h4>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">${service.price}</div>
                    <div className="text-sm text-gray-500">{service.duration}</div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Step 2: Date & Time Selection
  const DateTimeSelection = () => {
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);

    useEffect(() => {
      // Generate next 14 days
      const dates = [];
      for (let i = 1; i <= 14; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        dates.push(date);
      }
      setAvailableDates(dates);
    }, []);

    useEffect(() => {
      if (bookingData.selectedDate) {
        // Generate available time slots
        const times = [
          '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
          '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
        ];
        setAvailableTimes(times);
      }
    }, [bookingData.selectedDate]);

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Date & Time</h2>
          <p className="text-gray-600">Choose your preferred appointment slot</p>
        </div>

        {/* Selected Service Summary */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-900">{bookingData.selectedService?.name}</h3>
              <p className="text-sm text-gray-600">{bookingData.selectedService?.duration}</p>
            </div>
            <div className="text-lg font-bold text-blue-600">
              ${bookingData.selectedService?.price}
            </div>
          </div>
        </div>

        {/* Date Selection */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Dates</h3>
          <div className="grid grid-cols-7 gap-2">
            {availableDates.map(date => (
              <button
                key={date.toISOString()}
                onClick={() => setBookingData(prev => ({ ...prev, selectedDate: date }))}
                className={`p-3 rounded-lg border text-center transition-all ${
                  bookingData.selectedDate?.toDateString() === date.toDateString()
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-xs text-gray-500">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="font-semibold">
                  {date.getDate()}
                </div>
                <div className="text-xs text-gray-500">
                  {date.toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Time Selection */}
        {bookingData.selectedDate && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Times</h3>
            <div className="grid grid-cols-4 gap-3">
              {availableTimes.map(time => (
                <button
                  key={time}
                  onClick={() => {
                    setBookingData(prev => ({ ...prev, selectedTime: time }));
                    setCurrentStep(3);
                  }}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    bookingData.selectedTime === time
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Step 3: Patient Information
  const PatientInformation = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">User Information</h2>
        <p className="text-gray-600">Please provide your details for the appointment</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              required
              value={bookingData.patientInfo.name}
              onChange={(e) => setBookingData(prev => ({
                ...prev,
                patientInfo: { ...prev.patientInfo, name: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <input
              type="email"
              required
              value={bookingData.patientInfo.email}
              onChange={(e) => setBookingData(prev => ({
                ...prev,
                patientInfo: { ...prev.patientInfo, email: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
            <input
              type="tel"
              required
              value={bookingData.patientInfo.phone}
              onChange={(e) => setBookingData(prev => ({
                ...prev,
                patientInfo: { ...prev.patientInfo, phone: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
            <input
              type="date"
              required
              value={bookingData.patientInfo.dateOfBirth}
              onChange={(e) => setBookingData(prev => ({
                ...prev,
                patientInfo: { ...prev.patientInfo, dateOfBirth: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
          <select
            value={bookingData.patientInfo.gender}
            onChange={(e) => setBookingData(prev => ({
              ...prev,
              patientInfo: { ...prev.patientInfo, gender: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Symptoms or Concerns</label>
          <textarea
            value={bookingData.patientInfo.symptoms}
            onChange={(e) => setBookingData(prev => ({
              ...prev,
              patientInfo: { ...prev.patientInfo, symptoms: e.target.value }
            }))}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe your symptoms or the reason for this appointment..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
          <div className="flex space-x-4">
            {[
              { value: 'routine', label: 'Routine', color: 'green' },
              { value: 'urgent', label: 'Urgent', color: 'yellow' },
              { value: 'emergency', label: 'Emergency', color: 'red' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setBookingData(prev => ({
                  ...prev,
                  patientInfo: { ...prev.patientInfo, urgency: option.value }
                }))}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  bookingData.patientInfo.urgency === option.value
                    ? `border-${option.color}-500 bg-${option.color}-50 text-${option.color}-700`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={() => setCurrentStep(4)}
        disabled={!bookingData.patientInfo.name || !bookingData.patientInfo.email || !bookingData.patientInfo.phone}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        Continue to Payment
      </button>
    </div>
  );

  // Step 4: Payment & Confirmation
  const PaymentConfirmation = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment & Confirmation</h2>
        <p className="text-gray-600">Review your booking and complete payment</p>
      </div>

      {/* Booking Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h3>
        
        <div className="space-y-3 mb-6">
          <div className="flex justify-between">
            <span className="text-gray-600">Service:</span>
            <span className="font-medium">{bookingData.selectedService?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium">
              {bookingData.selectedDate?.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Time:</span>
            <span className="font-medium">{bookingData.selectedTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{bookingData.selectedService?.duration}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Service Fee:</span>
            <span className="font-medium">${bookingData.totalAmount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Platform Fee (15%):</span>
            <span className="font-medium">${bookingData.platformFee}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-blue-600 pt-2 border-t border-gray-200">
            <span>Total Amount:</span>
            <span>${bookingData.finalAmount}</span>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
        
        <div className="space-y-3">
          {[
            { id: 'card', label: 'Credit/Debit Card', icon: '💳' },
            { id: 'paypal', label: 'PayPal', icon: '🔵' },
            { id: 'apple-pay', label: 'Apple Pay', icon: '🍎' },
            { id: 'google-pay', label: 'Google Pay', icon: '🔴' }
          ].map(method => (
            <button
              key={method.id}
              onClick={() => setBookingData(prev => ({ ...prev, paymentMethod: method.id }))}
              className={`w-full p-4 rounded-lg border text-left flex items-center space-x-3 transition-all ${
                bookingData.paymentMethod === method.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-2xl">{method.icon}</span>
              <span className="font-medium">{method.label}</span>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={async () => {
          try {
            // Create appointment first
            const appointmentData = {
              doctor_id: provider?.id || 'demo-doctor-id', // You'll need to pass actual doctor/provider ID
              appointment_date: bookingData.selectedDate?.toISOString().split('T')[0],
              appointment_time: bookingData.selectedTime,
              service_name: bookingData.selectedService?.name,
              symptoms: bookingData.patientInfo.symptoms,
              patient_name: bookingData.patientInfo.name,
              patient_phone: bookingData.patientInfo.phone,
              patient_email: bookingData.patientInfo.email,
              consultation_fee: bookingData.finalAmount
            };

            // Here you would normally call your booking API
            // const bookingResponse = await axios.post(`${BACKEND_URL}/api/appointments`, appointmentData);
            
            alert('Booking confirmed! You can now message your healthcare provider.');
            navigate('/messages'); // Redirect to messages to show new conversation capability
          } catch (error) {
            console.error('Booking error:', error);
            alert('Booking completed! You will receive a confirmation email shortly.');
            navigate('/bookings');
          }
        }}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105"
      >
        Complete Booking - ${bookingData.finalAmount}
      </button>
    </div>
  );

  // Navigation
  const steps = [
    { number: 1, title: 'Select Service', component: <ServiceSelection /> },
    { number: 2, title: 'Date & Time', component: <DateTimeSelection /> },
    { number: 3, title: 'Patient Info', component: <PatientInformation /> },
    { number: 4, title: 'Payment', component: <PaymentConfirmation /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep >= step.number
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step.number}
                </div>
                <span className={`ml-2 font-medium ${
                  currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`mx-4 h-0.5 w-16 ${
                    currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Current Step Content */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          {steps[currentStep - 1].component}
        </div>

        {/* Navigation Buttons */}
        {currentStep > 1 && (
          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSystem;